import React, { Dispatch, SetStateAction, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { isDeepEqual } from '@fabric-ui/utils';
import { EntityProjectionName } from '@utils/entityProjectionNames';
import { ISyncStatus, useIsSettingSyncStatusBySettingsType } from '@views/Settings/hooks';
import { EntityBuildingBlock, EntityWeight, GetRiskScoringConfigResponse, ProjectionToBaseScore } from './types';
import { getRiskScoreProjectionConfig, RiskScorePermissions, useRiskScorePermissions } from './useRiskScorePermissions';
import { transformRiskScoringConfig } from './utils';

export interface RiskScoreContextType {
  entityWeights: EntityWeight[];
  setEntityWeights: Dispatch<SetStateAction<EntityWeight[]>>;
  buildingBlocks: EntityBuildingBlock[];
  setBuildingBlocks: Dispatch<SetStateAction<EntityBuildingBlock[]>>;
  editableBaseScores: ProjectionToBaseScore[];
  setEditableBaseScores: Dispatch<SetStateAction<ProjectionToBaseScore[]>>;
  initialWeights: EntityWeight[];
  initialBuildingBlocks: EntityBuildingBlock[];
  initialBaseScores: ProjectionToBaseScore[];
  isDirty: boolean;
  riskScoreProjectionName: EntityProjectionName;
  data: GetRiskScoringConfigResponse;
  getDisplayName: (projectionName: string) => string;
  refetch: () => Promise<any>;
  syncStatus: {
    allowOverride: boolean;
    setAllowOverrideSPF: Dispatch<SetStateAction<boolean>>;
    syncInfo: ISyncStatus;
    isSyncStatusLoading: boolean;
  };
  permissions: RiskScorePermissions;
}

const RiskScoreContext = React.createContext<RiskScoreContextType | null>(null);

export interface RiskScoreProviderProps {
  children: React.ReactNode;
  data: GetRiskScoringConfigResponse;
  riskScoreProjectionName: EntityProjectionName;
  refetch: () => Promise<any>;
}

export const RiskScoreProvider = ({ children, data, riskScoreProjectionName, refetch }: RiskScoreProviderProps) => {
  const {
    accountEntities: { aggProjs },
  } = useAvContext();

  const getDisplayName = useCallback((projectionName: string) => aggProjs[projectionName]?.projDisplayName ?? projectionName, [aggProjs]);

  const { entityWeights: initialWeights, buildingBlocks: initialBuildingBlocks } = useMemo(
    () => transformRiskScoringConfig({ data, riskScoreProjectionName, getDisplayName }),
    [data, riskScoreProjectionName, getDisplayName]
  );
  const initialBaseScores = data.projectionToBaseScores;

  const [entityWeights, setEntityWeights] = useState<EntityWeight[]>(initialWeights);
  const [buildingBlocks, setBuildingBlocks] = useState<EntityBuildingBlock[]>(initialBuildingBlocks);
  const [editableBaseScores, setEditableBaseScores] = useState<ProjectionToBaseScore[]>(initialBaseScores);

  useEffect(() => {
    setEntityWeights(initialWeights);
    setBuildingBlocks(initialBuildingBlocks);
    setEditableBaseScores(initialBaseScores);
  }, [initialWeights, initialBuildingBlocks, initialBaseScores]);
  const [allowOverrideSPF, setAllowOverrideSPF] = useState(false);

  const permissions = useRiskScorePermissions(riskScoreProjectionName, data.projectionToRiskScores);

  const { settingType } = getRiskScoreProjectionConfig(riskScoreProjectionName);
  const {
    data: syncInfo = { synced: false, configuredThrough: settingType, projectionToAffectedFields: [] },
    isLoading: isSyncStatusLoading,
  } = useIsSettingSyncStatusBySettingsType(settingType);
  const allowOverride = syncInfo.synced || allowOverrideSPF || isSyncStatusLoading;

  const isDirty = useMemo(
    () =>
      !isDeepEqual(entityWeights, initialWeights) ||
      !isDeepEqual(buildingBlocks, initialBuildingBlocks) ||
      !isDeepEqual(editableBaseScores, initialBaseScores),
    [entityWeights, initialWeights, buildingBlocks, initialBuildingBlocks, editableBaseScores, initialBaseScores]
  );

  const value = useMemo(
    () => ({
      entityWeights,
      setEntityWeights,
      buildingBlocks,
      setBuildingBlocks,
      editableBaseScores,
      setEditableBaseScores,
      initialWeights,
      initialBuildingBlocks,
      initialBaseScores,
      isDirty,
      riskScoreProjectionName,
      data,
      getDisplayName,
      refetch,
      syncStatus: {
        allowOverride,
        setAllowOverrideSPF,
        syncInfo,
        isSyncStatusLoading,
      },
      permissions,
    }),
    [
      entityWeights,
      buildingBlocks,
      editableBaseScores,
      initialWeights,
      initialBuildingBlocks,
      initialBaseScores,
      isDirty,
      riskScoreProjectionName,
      data,
      getDisplayName,
      refetch,
      allowOverride,
      setAllowOverrideSPF,
      syncInfo,
      isSyncStatusLoading,
      permissions,
    ]
  );

  return <RiskScoreContext value={value}>{children}</RiskScoreContext>;
};

export const useRiskScoreContext = (): RiskScoreContextType => {
  const ctx = use(RiskScoreContext);
  if (!ctx) {
    throw new Error('useRiskScoreContext must be used within a RiskScoreProvider');
  }
  return ctx;
};
